import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Grid, Snackbar, TextField, Typography } from '@mui/material'
import { contact } from '@/content/site'

interface FormState {
  name: string
  email: string
  company: string
  message: string
}
const EMPTY: FormState = { name: '', email: '', company: '', message: '' }
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Public key — safe in client code, and locked to true-forge.com at Web3Forms.
const WEB3FORMS_KEY = '2413916f-689c-4c0e-8626-6ce510e10b0a'

/** Inquiry form. Submits to Web3Forms, which emails Info@true-forge.com. */
export function ContactForm() {
  const [values, setValues] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Partial<FormState>>({})
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [failed, setFailed] = useState(false)

  const update = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setValues((v) => ({ ...v, [key]: e.target.value }))

  const validate = (): Partial<FormState> => {
    const next: Partial<FormState> = {}
    if (!values.name.trim()) next.name = 'Please enter your name'
    if (!EMAIL_RE.test(values.email)) next.email = 'Please enter a valid email'
    if (!values.message.trim()) next.message = 'Please tell us what you need'
    setErrors(next)
    return next
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const next = validate()
    const firstInvalid = (['name', 'email', 'message'] as const).find((k) => next[k])
    if (firstInvalid) {
      document.querySelector<HTMLElement>(`[name="${firstInvalid}"]`)?.focus()
      return
    }

    // a bot fills every field it finds, including the one humans can't see
    const form = e.target as HTMLFormElement
    if ((form.elements.namedItem('botcheck') as HTMLInputElement | null)?.checked) return

    setSending(true)
    setFailed(false)
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `New inquiry from ${values.name}${values.company ? ` (${values.company})` : ''}`,
          from_name: 'True Forge website',
          ...values,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data: { success?: boolean } = await res.json()
      if (!data.success) throw new Error('rejected')
      setSent(true)
      setValues(EMPTY)
      setErrors({})
    } catch {
      // never tell someone their inquiry arrived when it didn't
      setFailed(true)
    } finally {
      setSending(false)
    }
  }

  return (
    <Box component="form" noValidate onSubmit={onSubmit}>
      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField name="name" autoComplete="name" label="Name" value={values.name} onChange={update('name')} error={!!errors.name} helperText={errors.name} required />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField name="email" type="email" autoComplete="email" label="Email" value={values.email} onChange={update('email')} error={!!errors.email} helperText={errors.email} required />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField name="company" autoComplete="organization" label="Company" value={values.company} onChange={update('company')} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField name="message" label="Message" value={values.message} onChange={update('message')} error={!!errors.message} helperText={errors.message} required multiline minRows={5} />
        </Grid>
        <Grid size={{ xs: 12 }} sx={{ display: 'none' }} aria-hidden>
          <TextField name="botcheck" type="checkbox" tabIndex={-1} autoComplete="off" />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Button type="submit" variant="contained" size="large" disabled={sending} sx={{ minWidth: 200 }}>
            {sending ? 'Sending…' : 'Send Inquiry'}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 2 }}>
            This form is protected against spam. We’ll never share your details.
          </Typography>
        </Grid>
      </Grid>

      <Snackbar open={sent} autoHideDuration={5000} onClose={() => setSent(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled" onClose={() => setSent(false)}>
          Thank you — your inquiry has been received.
        </Alert>
      </Snackbar>

      <Snackbar open={failed} autoHideDuration={10000} onClose={() => setFailed(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" variant="filled" onClose={() => setFailed(false)}>
          Sorry — we couldn’t send that. Please email {contact.email} or call {contact.phone}.
        </Alert>
      </Snackbar>
    </Box>
  )
}
